'use client';
import { ChevronRight, Clock } from 'lucide-react';
import { sanitizeDescription } from '@/lib/sanitizeText';

/**
 * Список услуг на странице профи. Запись живёт в BookingDrawer (ТЗ 4) —
 * раньше здесь раскрывался аккордеон с датами и слотами, отсюда прежнее имя
 * файла ServiceBookingAccordion.
 *
 * Сама панель принадлежит родителю: её же открывает кнопка «Записаться»
 * в hero (ТЗ 6.1), поэтому владелец состояния должен быть один.
 *
 * Имя файла оставлено прежним намеренно: переименование — отдельное решение,
 * оно тянет за собой импорт в ProfileClient.
 */

interface Service {
  id: string;
  title: string;
  description?: string;
  price: number | string;
  durationMinutes?: number;
}

interface Props {
  services: Service[];
  /** Открыть панель записи с этой услугой — состояние держит родитель */
  onServiceClick: (serviceId: string) => void;
}

export default function ServicesList({ services, onServiceClick }: Props) {
  if (!services || services.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">
        Услуги не добавлены
      </div>
    );
  }

  return (
    <div className="divide-y divide-border-subtle">
        {services.map((svc) => (
          // Один аффорданс: строка кликабельна целиком, справа шеврон.
          // Радио-кружок убран вместе с аккордеоном — он обозначал раскрытие,
          // которого больше нет.
          <button
            key={svc.id}
            type="button"
            onClick={() => onServiceClick(svc.id)}
            className="w-full text-left px-4 py-4 flex items-start gap-3 transition-colors hover:bg-bg-subtle/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-text-primary text-sm leading-snug">{svc.title}</p>
              {svc.description && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-line">
                  {sanitizeDescription(svc.description)}
                </p>
              )}
              {svc.durationMinutes && (
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 mt-1">
                  <Clock size={10} />
                  {svc.durationMinutes} мин
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              <span className="text-base font-bold text-brand whitespace-nowrap">
                {Number(svc.price).toLocaleString('ru-RU')} ₽
              </span>
              <ChevronRight size={16} className="text-gray-400" />
            </div>
          </button>
        ))}
    </div>
  );
}
